"use client";

import NextLink from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { AuthLayout } from "@/components/ui/auth-layout";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, Label } from "@/components/ui/fieldset";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { Text, TextLink } from "@/components/ui/text";
import { ApiError, auth, setToken } from "@/lib/api";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showRegisteredBanner = searchParams.get("email") !== null && !error;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { access_token } = await auth.login(email, password);
      setToken(access_token);
      router.replace("/dashboard");
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Unexpected error");
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <NextLink href="/" className="mb-6 flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-lg bg-zinc-900 text-sm font-bold text-white dark:bg-white dark:text-zinc-900">
            I
          </div>
          <span className="text-sm font-semibold text-zinc-950 dark:text-white">
            INI Assistant
          </span>
        </NextLink>
        <Heading>Log in</Heading>
        <Text className="mt-1">
          Need an account? <TextLink href="/signup">Create one</TextLink>
        </Text>

        {showRegisteredBanner && (
          <p className="mt-6 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-300">
            Account created. Log in to continue.
          </p>
        )}

        <div className="mt-6">
          <GoogleSignInButton />
        </div>

        <form onSubmit={onSubmit} className="mt-4">
          <FieldGroup>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </Field>
            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </Field>
          </FieldGroup>

          <div className="mt-6 flex flex-col gap-2">
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? "Signing in…" : "Log in"}
            </Button>
            <TextLink href="/auth/forgot" className="text-center text-xs">
              Forgot your password?
            </TextLink>
          </div>

          {error && (
            <p className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              {error}
            </p>
          )}
        </form>
      </div>
    </AuthLayout>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
